import {Component, inject, Input, OnDestroy} from "@angular/core";
import {Store} from "@ngrx/store";
import {CopyFundReportAndValuationsActions, FundValuationActions} from "../../store/fund.actions";
import {selectFundIsArchived} from "../../store/fund/fund.selectors";
import {selectFundValuationDeleteError, selectSelectedFundValuationsForFundReport} from "../../store/fund-valuation/fund-valuation.selectors";
import {DELETE_ACCESS} from "../../../shared/model/login/auth.constants";
import {AuthenticationService} from "../../../shared/services/login/authentication.service";
import {ConfirmationComponent} from "../../../shared/components/confirmation/confirmation.component";
import {BehaviorSubject, combineLatest, map} from "rxjs";
import {Router} from "@angular/router";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {FundValuation} from "../../models/fund-valuation";
import {FundCopyWorkflowStepperComponent} from "../fund-copy-workflow-stepper/fund-copy-workflow-stepper.component";
import {Sort} from "@angular/material/sort";
import {CopyConfig, FundValuationCopyConfig} from "../../models/copy-config";
import {CodeTableEnum} from "../../../shared/model/code";
import {FundValuationCreateDialogComponent} from "../fund-valuation-create-dialog/fund-valuation-create-dialog.component";
import {sortByIsoDateBlanksLast} from "../../../shared/utils/sort-util";

@Component({
    selector: "valumize-fund-valuation-manage-dialog",
    templateUrl: "./fund-valuation-manage-dialog.component.html",
    styleUrls: ["./fund-valuation-manage-dialog.component.scss"],
    standalone: false
})
export class FundValuationManageDialogComponent implements OnDestroy {
    private readonly store = inject(Store);
    private readonly authService = inject(AuthenticationService);
    private readonly dialog = inject(MatDialog);
    private readonly currentDialog = inject(MatDialogRef<FundValuationManageDialogComponent>);
    private readonly router = inject(Router);

    @Input() fundId?: number;
    @Input() fundReportId?: number;
    @Input() currentFundValuationId?: number;
    @Input() returnToAsset = false;
    /** Overrides the dialog title when opened for a specific purpose (e.g. the algorithmic DMV picker). */
    @Input() title = "Manage Fund Valuation";
    /** Optional one-line explanation shown as an info banner — tells the user why the dialog opened. */
    @Input() contextMessage?: string;

    fundValuationsForFundReport$ = this.store.select(selectSelectedFundValuationsForFundReport);
    isArchived$ = this.store.select(selectFundIsArchived);
    fundValuationDeleteError$ = this.store.select(selectFundValuationDeleteError);
    columns = ["selected", "id", "valuationType", "closingDate", "description", "actions"];

    codeTableFundValuationType = CodeTableEnum.FUND_VALUATIONTYPE;

    /** Closing Date sort direction (WI-1239); default ascending, toggled via the column header. */
    sortDirection: "asc" | "desc" = "asc";
    private readonly sortDirection$ = new BehaviorSubject<"asc" | "desc">("asc");

    /**
     * The rows the table renders, sorted by Closing Date with missing dates last (WI-1239).
     *
     * Sorted HERE and not in the template: binding `[dataSource]="sortValuations(...)"` re-ran the sort on
     * every change-detection pass and handed mat-table a brand-new array reference each time, so it
     * re-rendered every row continuously. Deriving it once per data/direction change gives mat-table a stable
     * reference, and mirrors how the copy dialog already builds `displayedFundValuations$`.
     */
    displayedValuations$ = combineLatest([this.fundValuationsForFundReport$, this.sortDirection$]).pipe(
        map(([valuations, dir]) => sortByIsoDateBlanksLast(valuations.data, v => v.closingDate?.date, dir))
    );

    onSortChange(sort: Sort) {
        this.sortDirection = sort.direction === "desc" ? "desc" : "asc";
        this.sortDirection$.next(this.sortDirection);
    }

    selectAndReturn(fundValuationId: number): void {
        this.currentDialog.close(fundValuationId);
    }

    onRowClick(fundValuationId: number): void {
        if (this.returnToAsset) {
            this.selectAndReturn(fundValuationId);
        } else {
            if (!this.fundId || !this.fundReportId) {
                return;
            }
            this.currentDialog.close();
            this.router.navigate(["funds", this.fundId], {
                queryParams: {fundReportId: this.fundReportId, fundValuationId}
            });
        }
    }

    openCreateDialog(): void {
        if (!this.fundId || !this.fundReportId) {
            return;
        }
        this.dialog.open(FundValuationCreateDialogComponent, {
            data: {fundId: this.fundId, fundReportId: this.fundReportId}
        });
    }

    get showDeleteButton(): boolean {
        return this.authService.currentUser?.hasAnyPermission(DELETE_ACCESS) ?? false;
    }

    openDeleteConfirmation(fundValuationId: number): void {
        const dialogRef = this.dialog.open(ConfirmationComponent);

        dialogRef.componentInstance.confirmMessage = "Delete Fund Valuation";
        dialogRef.componentInstance.htmlContent = `
        <p>Are you sure you want to delete this fund valuation?</p>
        <p>Associated fund valuation scenarios, asset valuations, and asset valuation scenarios will also be deleted.</p>
        <p><strong>This action cannot be undone.</strong></p>`;

        dialogRef.afterClosed().pipe(
            map(result => {
                if (result && this.fundId !== undefined && this.fundReportId !== undefined) {
                    this.store.dispatch(FundValuationActions.delete({fundId: this.fundId, fundReportId: this.fundReportId, fundValuationId}));
                }
            })
        ).subscribe();
    }

    navigateToCopyWorkflowStepper(fundValuation: FundValuation) {
        const copyConfig = this.createCopyConfig(fundValuation);
        if (!copyConfig) {
            return;
        }

        const dialogRef = this.dialog.open(FundCopyWorkflowStepperComponent, {disableClose: true});
        dialogRef.componentInstance.startWith = "valuation";
        dialogRef.componentInstance.fundId = this.fundId;
        dialogRef.componentInstance.fundReportId = this.fundReportId;

        this.currentDialog.close();
    }

    private createCopyConfig(fundValuation: FundValuation) {
        const fundValuationId = fundValuation.id;
        const closingDate = fundValuation.closingDate?.date ?? "";
        const description = fundValuation.description?.text ?? "";
        if (!fundValuationId) {
            return;
        }

        const fundValuationCopyConfig: FundValuationCopyConfig = {
            fundValuationId,
            closingDate,
            description,
            copyUndrawnValuation: false,
            copyBidPriceAdjustment: false,
            assetValuationsCopyConfig: []
        }
        const copyConfig: CopyConfig = {fundValuationCopyConfig};

        this.store.dispatch(CopyFundReportAndValuationsActions.updateCopyConfig({copyConfig}));
        return copyConfig;
    }

    ngOnDestroy() {
        this.store.dispatch(FundValuationActions.clearDeleteError());
    }
}
